import { useCallback, useEffect, useRef, useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import './PhotoEditor.css'

/**
 * Position and size the crop before it is uploaded.
 *
 * A centre-crop is right often enough to be a good default and wrong often
 * enough to be annoying: someone standing to one side of a group shot ends up
 * cropped to a shoulder. So the picture is draggable inside a circular
 * viewport, with a zoom slider, and what you frame is exactly what is stored.
 *
 * The maths is one transform: `scale` and an offset in *source* pixels. The
 * preview and the final canvas apply the same numbers at different sizes, so
 * the output cannot disagree with what was on screen.
 */

/** Rendered size of the circular viewport. */
const VIEW = 260
/** Edge of the stored square. Covers a retina business card. */
const OUT = 512

interface Props {
  /** The chosen or captured image. */
  file: File
  onCancel: () => void
  onDone: (blob: Blob) => void
}

export function PhotoEditor({ file, onCancel, onDone }: Props) {
  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  // Centre of the crop, in source-image pixels.
  const [centre, setCentre] = useState({ x: 0, y: 0 })
  const canvas = useRef<HTMLCanvasElement>(null)
  const drag = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    let live = true
    let made: ImageBitmap | null = null
    createImageBitmap(file)
      .then((b) => {
        if (!live) {
          b.close()
          return
        }
        made = b
        setBitmap(b)
        setCentre({ x: b.width / 2, y: b.height / 2 })
      })
      .catch(() => live && setError('That file could not be read as an image.'))
    return () => {
      live = false
      made?.close()
    }
  }, [file])

  /**
   * How many source pixels the viewport spans at the current zoom.
   *
   * At zoom 1 the crop is the largest square that fits the image, which is the
   * centre-crop this replaces. Zooming in narrows it.
   */
  const span = bitmap ? Math.min(bitmap.width, bitmap.height) / zoom : 0

  /** Keep the crop inside the picture, so no edge shows blank. */
  const clamp = useCallback(
    (c: { x: number; y: number }) => {
      if (!bitmap) return c
      const half = span / 2
      return {
        x: Math.min(Math.max(c.x, half), bitmap.width - half),
        y: Math.min(Math.max(c.y, half), bitmap.height - half),
      }
    },
    [bitmap, span],
  )

  useEffect(() => setCentre((c) => clamp(c)), [clamp])

  // Draw the preview: the same crop rectangle the export will use.
  useEffect(() => {
    const ctx = canvas.current?.getContext('2d')
    if (!ctx || !bitmap) return
    ctx.clearRect(0, 0, VIEW, VIEW)
    ctx.drawImage(bitmap, centre.x - span / 2, centre.y - span / 2, span, span, 0, 0, VIEW, VIEW)
  }, [bitmap, centre, span])

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    drag.current = { x: e.clientX, y: e.clientY }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current || !bitmap) return
    // Screen pixels to source pixels: the viewport shows `span` across `VIEW`.
    const perPixel = span / VIEW
    const dx = (e.clientX - drag.current.x) * perPixel
    const dy = (e.clientY - drag.current.y) * perPixel
    drag.current = { x: e.clientX, y: e.clientY }
    // Minus: dragging the picture right moves the crop window left.
    setCentre((c) => clamp({ x: c.x - dx, y: c.y - dy }))
  }

  const endDrag = () => {
    drag.current = null
  }

  const apply = async () => {
    if (!bitmap) return
    const out = document.createElement('canvas')
    // Never upscale — a 200px source stays 200px rather than being blown up.
    const size = Math.min(Math.round(span), OUT)
    out.width = size
    out.height = size
    const ctx = out.getContext('2d')
    if (!ctx) {
      setError('This browser could not process the image.')
      return
    }
    ctx.drawImage(bitmap, centre.x - span / 2, centre.y - span / 2, span, span, 0, 0, size, size)
    const blob = await new Promise<Blob | null>((r) => out.toBlob(r, 'image/jpeg', 0.85))
    if (!blob) {
      setError('This browser could not encode the image.')
      return
    }
    onDone(blob)
  }

  return (
    <Modal
      title="Adjust photo"
      onClose={onCancel}
      width={340}
      footer={
        <>
          <button className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={() => void apply()} disabled={!bitmap}>
            Use photo
          </button>
        </>
      }
    >
      {error ? (
        <div className="recform__error">{error}</div>
      ) : (
        <div className="pedit">
          <div
            className="pedit__view"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <canvas ref={canvas} width={VIEW} height={VIEW} className="pedit__canvas" />
          </div>
          <input
            className="pedit__zoom"
            type="range"
            min={1}
            max={4}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            aria-label="Zoom"
          />
        </div>
      )}
    </Modal>
  )
}
